import React, { useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import Button from '../components/buttons/Button';
import { FaChartLine } from 'react-icons/fa';
import structuredDataRegistry from './structuredDataRegistry';

// Import Bullseye components
import { BullseyeDiagram, EchoReportView } from './Bullseye/BullseyeDiagram';
import { StressBullseyeView } from './Bullseye/StressBullseye';
import { IRMCardiaqueBullseyeView } from './Bullseye/IRMCardiaqueBullseye';
import EchoCodesManager from './EchoCodesManager';

// Import export functions
import { captureStressBullseyeFromDOM, captureStandardBullseyeFromDOM, captureIRMBullseyeFromDOM } from './renderStressBullseyeForExport';
import { createLogger } from '../services/Debug';

/**
 * Main component for managing structured data visualization
 *
 * @param {Object} props
 * @param {Object} props.structuredData - The structured data to visualize
 * @param {Function} props.onDataChange - Callback when data changes
 * @param {string} props.reportType - Report type (optional, for backward compatibility)
 * @param {string} props.reportText - Report text (for echo codes extraction)
 * @param {boolean} props.showButton - Whether to show the trigger button (default: true)
 * @param {React.Ref} props.buttonRef - Optional ref for the button
 */
const StructuredDataManager = forwardRef(({
  structuredData,
  onDataChange,
  reportType,
  reportText = '',
  showButton = true,
  buttonRef: externalButtonRef,
  onSetSidebarVisible,
  textareaRef,
  inlineMode = false
}, ref) => {
  const log = createLogger('StructuredDataManager');
  const internalButtonRef = useRef(null);
  const buttonRef = externalButtonRef || internalButtonRef;
  const [showVisualization, setShowVisualization] = React.useState(false);

  // Detect the type of structured data
  const dataTypeInfo = useMemo(() => {
    if (!structuredData) return null;
    
    // Parse the data (handles any necessary transformations)
    const parsedData = structuredDataRegistry.parseData(structuredData);
    
    // Detect the type
    const type = structuredDataRegistry.detectType(parsedData);
    
    return type ? {
      type,
      parsedData,
      component: type.component
    } : null;
  }, [structuredData]);

  const toggleVisualization = () => {
    setShowVisualization(prev => {
      const newState = !prev;
      return newState;
    });
  };

  // Handle close and focus textarea
  const handleClose = React.useCallback(() => {
    setShowVisualization(false);
    // Focus textarea after closing to allow immediate typing
    if (textareaRef && textareaRef.current) {
      setTimeout(() => {
        textareaRef.current.focus();
      }, 100);
    }
  }, [textareaRef]);

  // Export handlers for schema
  const handleCopySchema = React.useCallback(async () => {
    if (!dataTypeInfo) return false;
    
    try {
      let dataUrl = null;
      const { component } = dataTypeInfo;
      
      switch (component) {
        case 'StressBullseye':
          dataUrl = await captureStressBullseyeFromDOM({ scale: 4 });
          break;
        case 'IRMCardiaqueBullseye':
          dataUrl = await captureIRMBullseyeFromDOM({ scale: 5 });
          break;
        case 'StandardBullseye':
        default:
          dataUrl = await captureStandardBullseyeFromDOM({ scale: 4 });
          break;
      }
      
      if (!dataUrl) return false;
      
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const clipboardItem = new ClipboardItem({ "image/png": blob });
      await navigator.clipboard.write([clipboardItem]);
      return true;
    } catch (err) {
      log.error('Error copying schema:', err);
      return false;
    }
  }, [dataTypeInfo]);

  const handleDownloadSchema = React.useCallback(async () => {
    if (!dataTypeInfo) return false;
    
    try {
      let dataUrl = null;
      const { component } = dataTypeInfo;
      
      switch (component) {
        case 'StressBullseye':
          dataUrl = await captureStressBullseyeFromDOM({ scale: 4 });
          break;
        case 'IRMCardiaqueBullseye':
          dataUrl = await captureIRMBullseyeFromDOM({ scale: 5 });
          break;
        case 'StandardBullseye':
        default:
          dataUrl = await captureStandardBullseyeFromDOM({ scale: 4 });
          break;
      }
      
      if (!dataUrl) return false;
      
      const link = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      link.download = `schema_cinetique_${date}.png`;
      link.href = dataUrl;
      link.click();
      return true;
    } catch (err) {
      log.error('Error downloading schema:', err);
      return false;
    }
  }, [dataTypeInfo]);

  // Expose export functions via ref
  useImperativeHandle(ref, () => ({
    copySchema: handleCopySchema,
    downloadSchema: handleDownloadSchema,
    hasStructuredData: () => !!dataTypeInfo
  }), [handleCopySchema, handleDownloadSchema, dataTypeInfo]);

  // Don't render anything if no structured data is detected
  if (!dataTypeInfo) {
    return null;
  }

  // Render the appropriate visualization component
  const renderVisualization = () => {
    const { type, parsedData, component } = dataTypeInfo;
    const commonProps = inlineMode
      ? { inline: true, visible: true, onClose: handleClose }
      : { inline: false, anchorEl: buttonRef.current, visible: showVisualization, onClose: handleClose };

    switch (component) {
      case 'StressBullseye':
        return (
          <StressBullseyeView
            {...commonProps}
            structuredData={parsedData}
            onBullseyeChange={onDataChange}
          />
        );

      case 'IRMCardiaqueBullseye':
        return (
          <IRMCardiaqueBullseyeView
            {...commonProps}
            structuredData={parsedData}
            onBullseyeChange={onDataChange}
          />
        );

      case 'EchoCodesManager':
        return (
          <EchoCodesManager
            reportText={reportText}
            structuredData={parsedData}
            onChange={onDataChange}
            readOnly={false}
          />
        );

      case 'StandardBullseye':
      default:
        return (
          <EchoReportView
            {...commonProps}
            structuredData={parsedData}
            onBullseyeChange={onDataChange}
          />
        );
    }
  };

  // Get appropriate button label based on component type
  const getButtonLabel = () => {
    if (dataTypeInfo.component === 'EchoCodesManager') {
      return 'Codes Écho';
    }
    return 'Schéma cinétique';
  };

  return (
    <div className="structured-data-manager">
      {!inlineMode && showButton && (
        <Button
          icon={FaChartLine}
          variant="orange"
          ref={buttonRef}
          onClick={toggleVisualization}
          title={`Visualiser: ${dataTypeInfo.type.name}`}
        >
          {getButtonLabel()}
        </Button>
      )}
      {(inlineMode || showVisualization) && renderVisualization()}
    </div>
  );
});

StructuredDataManager.displayName = 'StructuredDataManager';
export default StructuredDataManager;

/**
 * Hook to check if data has structured content
 * Useful for conditional rendering in parent components
 */
export function useHasStructuredData(data) {
  return useMemo(() => {
    return structuredDataRegistry.hasStructuredData(data);
  }, [data]);
}

/**
 * Hook to get structured data type information
 */
export function useStructuredDataType(data) {
  return useMemo(() => {
    if (!data) return null;
    const type = structuredDataRegistry.detectType(data);
    return type ? {
      id: type.id,
      name: type.name,
      component: type.component
    } : null;
  }, [data]);
}